/**
 * One-item-at-a-time carousel. Slides scroll natively (CSS scroll-snap on
 * [data-track]); this keeps the "current" number in sync, moves one slide on
 * [data-prev] / [data-next], and disables the arrow at either end.
 * Used by the empty cart recommendations (snippets/pacc-cart-recommendations.liquid).
 */
class PaccCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.current = this.querySelector('[data-current]');
    if (!this.track) return;

    this.prev?.addEventListener('click', this.#onPrev);
    this.next?.addEventListener('click', this.#onNext);
    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.#onPrev);
    this.next?.removeEventListener('click', this.#onNext);
    this.track?.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.frame);
  }

  get #count() {
    return this.track.children.length;
  }

  get #index() {
    const width = this.track.clientWidth || 1;
    return Math.round(Math.abs(this.track.scrollLeft) / width);
  }

  /** @param {number} index */
  goTo(index) {
    const target = Math.max(0, Math.min(index, this.#count - 1));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({
      left: target * this.track.clientWidth * direction,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  #onPrev = () => this.goTo(this.#index - 1);

  #onNext = () => this.goTo(this.#index + 1);

  #onScroll = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const index = this.#index;
    if (this.current) this.current.textContent = String(index + 1);
    if (this.prev) this.prev.disabled = index <= 0;
    if (this.next) this.next.disabled = index >= this.#count - 1;
  }
}

if (!customElements.get('pacc-carousel')) {
  customElements.define('pacc-carousel', PaccCarousel);
}
